import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';


interface PatientMenuButtonProps {
    ptId: string;
}

const PatientMenuButton: React.FC<PatientMenuButtonProps> = ({ ptId }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [streamedContent, setStreamedContent] = useState('');
    const [loading, setLoading] = useState(false);

    const apiHost = process.env.REACT_APP_API_ENDPOINT?.startsWith('http')
        ? process.env.REACT_APP_API_ENDPOINT
        : `https://${process.env.REACT_APP_API_ENDPOINT}`;

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenuItemClick = (type: 'Summary' | 'SBAR') => {
        setDialogTitle(type);
        setDialogOpen(true);
        setStreamedContent('');
        setLoading(true);
        setAnchorEl(null);
        streamApiContent(type);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setStreamedContent('');
        setLoading(false);
    };

    const streamApiContent = async (type: 'Summary' | 'SBAR') => {
        const endpoint =
            type === 'Summary'
                ? `${apiHost}/AI/PatientSummary?ptId=${encodeURIComponent(ptId)}`
                : `${apiHost}/AI/SBAR?ptId=${encodeURIComponent(ptId)}`;

        try {
            const response = await fetch(endpoint);
            if (!response.body) throw new Error('No response body');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            function read() {
                reader.read().then(({ done, value }) => {
                    if (done) {
                        setLoading(false);
                        return;
                    }
                    buffer += decoder.decode(value, { stream: true });
                    setStreamedContent(buffer);
                    read();
                });
            }
            read();
        } catch (error) {
            setStreamedContent('Failed to retrieve data: ' + error);
            setLoading(false);
        }
    };

    function formatMarkdown(text: string): string {
        // Bold (**text**)
        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        // Line breaks
        text = text.replace(/\n/g, '<br/>');
        return text;
    }

    return (
        <span style={{ marginLeft: 8 }}>
            <Button
                aria-controls="simple-menu"
                aria-haspopup="true"
                variant="contained"
                color="default"
                size="small"
                onClick={handleMenuClick}
            >
                <i className="bi bi-stars"></i>
                AI
            </Button>
            <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleMenuItemClick('Summary')}>Patient Summary</MenuItem>
                <MenuItem onClick={() => handleMenuItemClick('SBAR')}>SBAR</MenuItem>
            </Menu>
            <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    {loading && <CircularProgress />}
                    <div
                        style={{ whiteSpace: 'pre-wrap', marginTop: 16 }}
                        dangerouslySetInnerHTML={{ __html: formatMarkdown(streamedContent) }}
                    />
                    <Alert
                        severity="warning"
                        style={{ fontSize: '0.9em', marginTop: 24 }}
                    >
                        <strong>Disclaimer:</strong> This summary is generated by an AI system for informational purposes only. It is not a substitute for professional medical judgment. The treating provider is solely responsible for all clinical decisions.
                    </Alert>
                </DialogContent>
            </Dialog>
        </span>
    );
};

export default PatientMenuButton;